<template>
  <!-- data-app="true" so Vuetify's overlay system (v-dialog) finds an anchor
       when this page is rendered outside v-app. -->
  <div class="daemon-page" data-app="true">
    <v-container class="daemon-shell">
      <header class="daemon-header">
        <div class="daemon-header__main">
          <v-btn
            text
            small
            color="primary"
            class="daemon-back"
            @click="goBack"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            กลับ
          </v-btn>
          <img
            :src="logoUrl"
            alt="Phasi Charoen"
            class="daemon-brand"
            decoding="async"
          />
          <div class="daemon-kicker">
            <v-icon size="17" color="primary">mdi-database-cog</v-icon>
            <span>eTax FoxPro Daemon</span>
            <span class="daemon-kicker__dot" aria-hidden="true"></span>
            <span class="daemon-kicker__step">จัดการการผูก</span>
          </div>
          <h1>บริษัทที่ผูกกับ daemon</h1>
          <p>ดูบริษัทที่ daemon ของคุณกำลังดูแลอยู่ ยกเลิกการผูกทีละบริษัทได้โดยไม่กระทบบริษัทอื่นในเครื่องเดียวกัน</p>
        </div>

        <div class="daemon-header__aside">
          <div v-if="currentUserEmail" class="daemon-account" aria-label="Current account">
            <span class="daemon-account__dot" />
            <span class="daemon-account__email">{{ currentUserEmail }}</span>
            <v-btn
              icon
              x-small
              class="tw-ml-2"
              :loading="loggingOut"
              :title="'ออกจากระบบ'"
              @click="logout"
            >
              <v-icon x-small>mdi-logout</v-icon>
            </v-btn>
          </div>
        </div>
      </header>

      <section class="daemon-stats" aria-label="Daemon binding summary">
        <div class="daemon-stat">
          <div class="daemon-stat__icon daemon-stat__icon--primary">
            <v-icon size="22" color="primary">mdi-domain</v-icon>
          </div>
          <div>
            <div class="daemon-stat__value">{{ totalBindings }}</div>
            <div class="daemon-stat__label">บริษัทที่ผูกอยู่</div>
          </div>
        </div>
        <div class="daemon-stat">
          <div class="daemon-stat__icon daemon-stat__icon--cyan">
            <v-icon size="22" color="cyan darken-2">mdi-server-network</v-icon>
          </div>
          <div>
            <div class="daemon-stat__value">{{ workerGroups.length }}</div>
            <div class="daemon-stat__label">daemon worker</div>
          </div>
        </div>
        <div class="daemon-stat">
          <div class="daemon-stat__icon daemon-stat__icon--success">
            <v-icon size="22" color="success">mdi-lan-connect</v-icon>
          </div>
          <div>
            <div class="daemon-stat__value">{{ statusCounts.online }}</div>
            <div class="daemon-stat__label">ออนไลน์</div>
          </div>
        </div>
        <div class="daemon-stat">
          <div class="daemon-stat__icon daemon-stat__icon--warning">
            <v-icon size="22" color="amber darken-3">mdi-alert-outline</v-icon>
          </div>
          <div>
            <div class="daemon-stat__value">{{ statusCounts.needsAttention }}</div>
            <div class="daemon-stat__label">ต้องตรวจสอบ</div>
          </div>
        </div>
      </section>

      <v-card class="daemon-panel" elevation="0">
        <div class="daemon-panel__header">
          <div class="daemon-panel__icon">
            <v-icon size="24" color="primary">mdi-link-variant</v-icon>
          </div>
          <div>
            <h2>การผูกที่ใช้งานอยู่</h2>
            <p>
              <template v-if="!loading">
                <strong>{{ totalBindings }}</strong> บริษัท ผ่าน
                <strong>{{ workerGroups.length }}</strong> daemon
              </template>
              <template v-else>กำลังโหลด…</template>
            </p>
          </div>
          <v-btn
            outlined
            small
            color="primary"
            class="daemon-refresh"
            :loading="loading"
            @click="loadBindings"
          >
            <v-icon left small>mdi-refresh</v-icon>
            รีเฟรชข้อมูล
          </v-btn>
        </div>

        <div class="daemon-toolbar">
          <v-text-field
            v-model.trim="query"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="ค้นหาบริษัท, worker, basePath"
            class="daemon-search"
          />

          <div class="daemon-filter-tabs" role="tablist" aria-label="Filter daemon status">
            <button
              v-for="option in statusOptions"
              :key="option.value"
              type="button"
              class="daemon-filter-tab"
              :class="{ 'daemon-filter-tab--active': statusFilter === option.value }"
              :aria-selected="statusFilter === option.value ? 'true' : 'false'"
              role="tab"
              @click="statusFilter = option.value"
            >
              <v-icon size="15" :color="statusFilter === option.value ? option.activeColor : option.color">
                {{ option.icon }}
              </v-icon>
              <span>{{ option.label }}</span>
              <strong>{{ option.count }}</strong>
            </button>
          </div>
        </div>

        <v-card-text class="daemon-panel__body">
          <transition name="daemon-fade">
            <v-alert
              v-if="error"
              type="error"
              outlined
              dense
              class="daemon-alert"
              role="alert"
            >
              <div class="daemon-alert__body">
                <span>{{ error }}</span>
                <v-btn
                  text
                  x-small
                  color="error"
                  aria-label="ปิดข้อความ"
                  @click="error = null"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-alert>
          </transition>

          <transition name="daemon-fade">
            <v-alert
              v-if="lastUnboundMessage"
              type="success"
              outlined
              dense
              class="daemon-alert"
              role="status"
            >
              <div class="daemon-alert__body">
                <span>{{ lastUnboundMessage }}</span>
                <v-btn
                  text
                  x-small
                  color="success"
                  aria-label="ปิดข้อความ"
                  @click="lastUnboundMessage = ''"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-alert>
          </transition>

          <div
            v-if="loading"
            class="daemon-skeletons"
            role="status"
            aria-live="polite"
            aria-label="กำลังโหลดรายการบริษัท"
          >
            <div
              v-for="n in 2"
              :key="`skeleton-worker-${n}`"
              class="daemon-skeleton-worker"
            >
              <div class="daemon-skeleton-worker__header">
                <div class="daemon-skeleton-worker__title">
                  <div class="daemon-skeleton daemon-skeleton--icon"></div>
                  <div class="daemon-skeleton-worker__title-text">
                    <div class="daemon-skeleton daemon-skeleton--text daemon-skeleton--w-180"></div>
                    <div class="daemon-skeleton daemon-skeleton--text daemon-skeleton--w-110 daemon-skeleton--sm"></div>
                  </div>
                </div>
                <div class="daemon-skeleton-worker__meta">
                  <div class="daemon-skeleton daemon-skeleton--pill"></div>
                  <div class="daemon-skeleton daemon-skeleton--pill daemon-skeleton--pill-sm"></div>
                </div>
              </div>
              <div
                v-for="r in (n === 1 ? 3 : 2)"
                :key="`skeleton-row-${n}-${r}`"
                class="daemon-skeleton-row"
              >
                <div class="daemon-skeleton-row__main">
                  <div class="daemon-skeleton daemon-skeleton--badge"></div>
                  <div class="daemon-skeleton-row__lines">
                    <div class="daemon-skeleton daemon-skeleton--text daemon-skeleton--w-220"></div>
                    <div class="daemon-skeleton daemon-skeleton--text daemon-skeleton--w-160 daemon-skeleton--sm"></div>
                  </div>
                </div>
                <div class="daemon-skeleton daemon-skeleton--button"></div>
              </div>
            </div>
            <span class="daemon-skeletons__sr">กำลังโหลดรายการบริษัท…</span>
          </div>

          <div
            v-else-if="workerGroups.length === 0"
            class="daemon-state"
          >
            <div class="daemon-state__icon daemon-state__icon--muted">
              <v-icon size="24" color="grey darken-1">mdi-link-variant-off</v-icon>
            </div>
            <div>
              <h3>ยังไม่มีบริษัทผูกกับ daemon ของคุณ</h3>
              <p>
                เริ่มจับคู่ daemon ใหม่ แล้วรัน CLI บนเครื่องที่จะใช้
              </p>
              <v-btn
                depressed
                small
                color="primary"
                class="daemon-state__action daemon-state__action--primary"
                @click="$router.push('/daemon/link')"
              >
                <v-icon left small>mdi-link-plus</v-icon>
                จับคู่ daemon ใหม่
              </v-btn>
            </div>
          </div>

          <div
            v-else-if="filteredWorkerGroups.length === 0"
            class="daemon-state"
          >
            <div class="daemon-state__icon daemon-state__icon--muted">
              <v-icon size="24" color="grey darken-1">mdi-filter-off-outline</v-icon>
            </div>
            <div>
              <h3>ไม่พบรายการที่ตรงกับตัวกรอง</h3>
              <p>
                ลองล้างคำค้นหา หรือเปลี่ยนตัวกรองสถานะจาก
                <strong>{{ selectedStatusLabel }}</strong>
              </p>
              <v-btn
                text
                small
                color="primary"
                class="daemon-state__action"
                @click="clearFilters"
              >
                <v-icon left small>mdi-close-circle-outline</v-icon>
                ล้างตัวกรอง
              </v-btn>
            </div>
          </div>

          <div v-else class="daemon-workers">
            <section
              v-for="g in filteredWorkerGroups"
              :key="g.workerId"
              class="daemon-worker"
              :class="'daemon-worker--' + presenceStatus(g)"
              :aria-label="`Worker ${g.workerId}`"
            >
              <header class="daemon-worker__header">
                <div class="daemon-worker__title">
                  <div class="daemon-worker__icon" aria-hidden="true">
                    <v-icon size="19" :color="presenceTone(g)">{{ presenceIcon(g) }}</v-icon>
                  </div>
                  <div class="daemon-worker__title-text">
                    <div class="daemon-worker__name" :title="workerMachineName(g)">
                      {{ workerMachineName(g) }}
                    </div>
                    <div class="daemon-worker__sub">
                      <code class="daemon-inline-code">{{ shortWorkerId(g.workerId) }}</code>
                    </div>
                  </div>
                </div>
                <div class="daemon-worker__meta">
                  <div class="daemon-status-pill" :class="'daemon-status-pill--' + presenceStatus(g)">
                    <span class="daemon-status-pill__dot" aria-hidden="true"></span>
                    {{ presenceLabel(g) }}
                  </div>
                  <div class="daemon-worker__count">
                    <strong>{{ g.rows.length }}</strong>
                    <span>บริษัท</span>
                  </div>
                </div>
              </header>

              <ul class="daemon-binding-list">
                <li
                  v-for="row in g.rows"
                  :key="row.companyId"
                  class="daemon-binding"
                >
                  <div class="daemon-binding__main">
                    <div class="daemon-binding__title-row">
                      <div class="daemon-binding__badge">
                        <v-icon size="16" color="primary">mdi-domain</v-icon>
                      </div>
                      <div class="daemon-binding__name" :title="row.companyName">
                        {{ row.companyName }}
                      </div>
                    </div>
                    <div class="daemon-binding__meta">
                      <span class="daemon-binding__label">
                        <v-icon size="13" color="grey darken-1">mdi-folder-key-outline</v-icon>
                        basePath
                      </span>
                      <code class="daemon-inline-code">{{ row.basePath || '—' }}</code>
                    </div>
                  </div>
                  <v-btn
                    outlined
                    small
                    color="error"
                    class="daemon-binding__unbind"
                    :loading="unbinding[row.companyId] === true"
                    :disabled="confirming !== null"
                    :aria-label="`ยกเลิกการผูก ${row.companyName}`"
                    @click="askUnbind(row)"
                  >
                    <v-icon left small>mdi-link-variant-off</v-icon>
                    ยกเลิก
                  </v-btn>
                </li>
              </ul>
            </section>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <v-dialog
      :value="confirming !== null"
      max-width="560"
      persistent
      @input="onDialogInput"
    >
      <v-card v-if="confirming" class="daemon-confirm">
        <v-card-title class="daemon-confirm__title">
          <div class="daemon-confirm__title-icon" aria-hidden="true">
            <v-icon color="error">mdi-link-variant-off</v-icon>
          </div>
          <span>ยกเลิกการผูก daemon?</span>
        </v-card-title>
        <v-card-text class="daemon-confirm__body">
          <p class="daemon-confirm__lede">
            บริษัท <strong>{{ confirming.companyName }}</strong> จะไม่ถูก daemon
            <code class="daemon-inline-code">{{ shortWorkerId(confirming.workerId) }}</code>
            ดูแลอีกต่อไป
          </p>
          <div class="daemon-confirm__row">
            <span>basePath ปัจจุบัน</span>
            <code class="daemon-inline-code">{{ confirming.basePath || '—' }}</code>
          </div>
          <p class="daemon-confirm__hint">
            <v-icon size="15" color="grey darken-1">mdi-information-outline</v-icon>
            <span>
              ระบบจะปรับ <code class="daemon-inline-code">storageMode</code> กลับเป็น
              <code class="daemon-inline-code">smb</code> — daemon รอบถัดไปจะหยุดดึงข้อมูลของบริษัทนี้
            </span>
          </p>
        </v-card-text>
        <v-card-actions class="daemon-confirm__actions">
          <v-spacer />
          <v-btn
            text
            class="daemon-confirm__dismiss"
            :disabled="unbinding[confirming.companyId] === true"
            @click="cancelUnbind"
          >
            ปิด
          </v-btn>
          <v-btn
            color="error"
            depressed
            class="daemon-confirm__submit"
            :loading="unbinding[confirming.companyId] === true"
            @click="confirmUnbind"
          >
            <v-icon left small>mdi-link-variant-off</v-icon>
            ยืนยันยกเลิกการผูก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import fc from '@/firebaseConfig';
import logoUrl from '@/assets/logo_phasi_charoen.svg';

const STALENESS_MS = 10 * 60 * 1000;

export default {
  name: 'DaemonManage',
  data() {
    return {
      logoUrl,
      loading: true,
      error: null,
      loggingOut: false,
      // Flat list of bound companies. Grouped into workerGroups for render.
      bindings: [],
      // workerId → { machineName, capabilities, presence, presenceLoaded }
      workers: {},
      // Re-evaluated every 30s so the presence label flips from "online" to
      // "heartbeat ค้าง" when the RTDB heartbeat goes silent.
      now: Date.now(),
      // companyId → true while its unbind write is in flight.
      unbinding: {},
      // The binding row currently shown in the confirm dialog, or null.
      confirming: null,
      lastUnboundMessage: '',
      query: '',
      statusFilter: 'all',
    };
  },
  computed: {
    currentUid() {
      const u = firebase.auth().currentUser;
      return u ? u.uid : null;
    },
    currentUserEmail() {
      const u = firebase.auth().currentUser;
      return u ? (u.email || u.uid) : null;
    },
    workerGroups() {
      const byId = new Map();
      for (const b of this.bindings) {
        if (!byId.has(b.workerId)) byId.set(b.workerId, []);
        byId.get(b.workerId).push(b);
      }
      const groups = [];
      for (const [workerId, rows] of byId.entries()) {
        rows.sort((a, b) => a.companyName.localeCompare(b.companyName, 'th'));
        groups.push({ workerId, rows });
      }
      // Stable order: most-bindings first, then by workerId for determinism.
      groups.sort((a, b) => (b.rows.length - a.rows.length) || a.workerId.localeCompare(b.workerId));
      return groups;
    },
    totalBindings() {
      return this.bindings.length;
    },
    statusCounts() {
      const counts = {
        online: 0,
        stale: 0,
        offline: 0,
        checking: 0,
        needsAttention: 0,
      };
      for (const g of this.workerGroups) {
        const status = this.presenceStatus(g);
        counts[status] = (counts[status] || 0) + 1;
      }
      counts.needsAttention = counts.stale + counts.offline;
      return counts;
    },
    statusOptions() {
      return [
        {
          value: 'all',
          label: 'ทั้งหมด',
          count: this.workerGroups.length,
          icon: 'mdi-view-dashboard-outline',
          color: 'grey darken-1',
          activeColor: 'white',
        },
        {
          value: 'online',
          label: 'ออนไลน์',
          count: this.statusCounts.online,
          icon: 'mdi-lan-connect',
          color: 'success',
          activeColor: 'white',
        },
        {
          value: 'stale',
          label: 'ค้าง',
          count: this.statusCounts.stale,
          icon: 'mdi-lan-pending',
          color: 'amber darken-3',
          activeColor: 'white',
        },
        {
          value: 'offline',
          label: 'ออฟไลน์',
          count: this.statusCounts.offline,
          icon: 'mdi-lan-disconnect',
          color: 'grey darken-1',
          activeColor: 'white',
        },
        {
          value: 'checking',
          label: 'ตรวจสอบ',
          count: this.statusCounts.checking,
          icon: 'mdi-progress-clock',
          color: 'grey darken-1',
          activeColor: 'white',
        },
      ];
    },
    selectedStatusLabel() {
      const option = this.statusOptions.find(o => o.value === this.statusFilter);
      return option ? option.label : 'ทั้งหมด';
    },
    filteredWorkerGroups() {
      const q = (this.query || '').trim().toLowerCase();
      const groups = [];
      for (const g of this.workerGroups) {
        const status = this.presenceStatus(g);
        if (this.statusFilter !== 'all' && status !== this.statusFilter) continue;
        if (!q) {
          groups.push(g);
          continue;
        }

        const workerText = [
          g.workerId,
          this.workerMachineName(g),
          this.presenceLabel(g),
        ].join(' ').toLowerCase();
        if (workerText.includes(q)) {
          groups.push(g);
          continue;
        }

        const rows = g.rows.filter(row => this._rowMatchesQuery(row, q));
        if (rows.length > 0) groups.push({ ...g, rows });
      }
      return groups;
    },
  },
  mounted() {
    if (!this.currentUid) {
      const redirect = encodeURIComponent(this.$route.fullPath);
      this.$router.replace(`/login?redirect=${redirect}`);
      return;
    }
    this.loadBindings();
    this._nowTimer = setInterval(() => { this.now = Date.now(); }, 30000);
  },
  beforeDestroy() {
    this._teardownPresence();
    if (this._nowTimer) { clearInterval(this._nowTimer); this._nowTimer = null; }
    if (this._unboundTimer) { clearTimeout(this._unboundTimer); this._unboundTimer = null; }
  },
  methods: {
    async loadBindings() {
      const uid = this.currentUid;
      if (!uid) return;
      this.loading = true;
      this.error = null;
      this._teardownPresence();
      this.workers = {};
      try {
        const snap = await fc.db
          .collection('CompaniesUsers')
          .where('userID', '==', uid)
          .where('isAdmin', '==', true)
          .get();
        const candidates = [];
        snap.forEach((doc) => {
          const d = doc.data();
          if (d && d.companyID) candidates.push(d);
        });

        // Access gate: this page is dev-tooling for managing daemon worker
        // bindings, restricted to internal engineers only. isEngineer is a
        // global role on the Users document, so we read it directly rather
        // than trusting Vuex (which may be empty on a hard refresh / deep
        // link straight to this route).
        const userSnap = await fc.users.doc(uid).get();
        const isEngineer = userSnap.exists && userSnap.data()?.isEngineer === true;
        if (!isEngineer) {
          this.$router.replace('/companies-list');
          return;
        }

        // Read FoxproConfig/config for every admin company and keep only the
        // ones daemon-bound to THIS user. N reads on the admin list is fine
        // for a management page; switching to a collectionGroup query would
        // require an index and security-rules audit we don't need yet.
        const results = await Promise.all(candidates.map(async (c) => {
          try {
            const cfgSnap = await fc.companies
              .doc(c.companyID)
              .collection('FoxproConfig')
              .doc('config')
              .get();
            if (!cfgSnap.exists) return null;
            const cfg = cfgSnap.data() || {};
            if (cfg.storageMode !== 'daemon') return null;
            if (cfg.daemonUserId !== uid) return null;
            if (!cfg.daemonWorkerId) return null;
            return {
              companyId: c.companyID,
              companyName: this._companyLabel(c),
              workerId: cfg.daemonWorkerId,
              basePath: typeof cfg.basePath === 'string' ? cfg.basePath : '',
            };
          } catch (e) {
            return null;
          }
        }));
        this.bindings = results.filter(Boolean);
        this._subscribePresence();
      } catch (err) {
        this.error = `โหลดรายการบริษัทไม่สำเร็จ: ${err.message}`;
      } finally {
        this.loading = false;
      }
    },
    _companyLabel(c) {
      const co = c.company || {};
      // Prefer Thai name; fall back to English; last resort the companyID.
      return co.nameTH || co.nameEN || c.companyID;
    },
    clearFilters() {
      this.query = '';
      this.statusFilter = 'all';
    },
    _rowMatchesQuery(row, q) {
      return [
        row.companyId,
        row.companyName,
        row.basePath,
        row.workerId,
      ].some(v => String(v || '').toLowerCase().includes(q));
    },
    _subscribePresence() {
      const uid = this.currentUid;
      if (!uid) return;
      const seen = new Set();
      for (const b of this.bindings) {
        if (seen.has(b.workerId)) continue;
        seen.add(b.workerId);
        // Declare every reactive field up-front so Vue 2 picks them up — the
        // RTDB callback mutates `data` and `presenceLoaded` later.
        this.$set(this.workers, b.workerId, {
          data: null,
          presenceLoaded: false,
        });
        const ref = fc.rtdb.ref(`workers/${uid}/${b.workerId}`);
        const cb = ref.on('value', (snap) => {
          const slot = this.workers[b.workerId];
          if (!slot) return;
          slot.data = snap.val();
          slot.presenceLoaded = true;
        }, () => {
          const slot = this.workers[b.workerId];
          if (!slot) return;
          slot.presenceLoaded = true;
        });
        if (!this._presenceSubs) this._presenceSubs = [];
        this._presenceSubs.push({ ref, cb });
      }
    },
    _teardownPresence() {
      const subs = this._presenceSubs || [];
      for (const s of subs) {
        try { s.ref.off('value', s.cb); } catch (e) { /* */ }
      }
      this._presenceSubs = [];
    },
    workerMachineName(g) {
      const w = this.workers[g.workerId];
      if (w && w.data && w.data.machineName) return w.data.machineName;
      return 'Daemon worker';
    },
    presenceStatus(g) {
      const w = this.workers[g.workerId];
      if (!w || !w.presenceLoaded) return 'checking';
      const data = w.data;
      if (!data) return 'offline';
      if (data.status === 'offline') return 'offline';
      const lastSeen = Number(data.lastSeen || 0);
      if (lastSeen > 0 && (this.now - lastSeen) > STALENESS_MS) return 'stale';
      return 'online';
    },
    presenceLabel(g) {
      switch (this.presenceStatus(g)) {
        case 'online':   return 'ออนไลน์';
        case 'stale':    return 'heartbeat ค้าง';
        case 'offline':  return 'ออฟไลน์';
        case 'checking':
        default:         return 'กำลังตรวจสอบ…';
      }
    },
    presenceTone(g) {
      switch (this.presenceStatus(g)) {
        case 'online':  return 'success';
        case 'stale':   return 'amber darken-3';
        case 'offline': return 'grey darken-1';
        default:        return 'grey darken-1';
      }
    },
    presenceIcon(g) {
      switch (this.presenceStatus(g)) {
        case 'online':  return 'mdi-lan-connect';
        case 'stale':   return 'mdi-lan-pending';
        case 'offline': return 'mdi-lan-disconnect';
        default:        return 'mdi-progress-clock';
      }
    },
    shortWorkerId(id) {
      if (!id) return '';
      if (id.length <= 12) return id;
      return id.slice(0, 6) + '…' + id.slice(-4);
    },
    askUnbind(row) {
      this.confirming = row;
    },
    cancelUnbind() {
      if (this.confirming && this.unbinding[this.confirming.companyId]) return;
      this.confirming = null;
    },
    onDialogInput(v) {
      // v-dialog fires `input(false)` on outside-click / esc. Treat it as cancel
      // unless a write is in flight (persistent already blocks the outside-click,
      // but esc can still slip through on some Vuetify builds).
      if (!v) this.cancelUnbind();
    },
    async confirmUnbind() {
      const row = this.confirming;
      if (!row) return;
      const uid = this.currentUid;
      if (!uid) return;
      this.$set(this.unbinding, row.companyId, true);
      this.error = null;
      try {
        const FieldValue = firebase.firestore.FieldValue;
        await fc.companies
          .doc(row.companyId)
          .collection('FoxproConfig')
          .doc('config')
          .update({
            // 'smb' is the codebase's default non-daemon storage mode (see
            // setting-ap-prompt.vue line ~2825 and the duplicate-company
            // config template). Reverting to it leaves the company in a valid
            // state instead of deleting the doc entirely.
            storageMode: 'smb',
            daemonUserId: FieldValue.delete(),
            daemonWorkerId: FieldValue.delete(),
            basePath: FieldValue.delete(),
            unboundAt: FieldValue.serverTimestamp(),
            unboundBy: uid,
          });
        // Drop the row locally. If the worker group is now empty, the unused
        // RTDB subscription stays alive until next loadBindings — cheap, and
        // refreshing or leaving the page tears it down.
        this.bindings = this.bindings.filter(b => b.companyId !== row.companyId);
        this.lastUnboundMessage = `ยกเลิกการผูก ${row.companyName} เรียบร้อย`;
        if (this._unboundTimer) clearTimeout(this._unboundTimer);
        this._unboundTimer = setTimeout(() => {
          this.lastUnboundMessage = '';
          this._unboundTimer = null;
        }, 4500);
        this.confirming = null;
      } catch (err) {
        this.error = `ยกเลิกไม่สำเร็จ: ${err.message}`;
      } finally {
        this.$set(this.unbinding, row.companyId, false);
      }
    },
    goBack() {
      // Prefer browser history so the user lands where they came from.
      // Fallback to /companies-list when this page was opened in a fresh tab
      // (e.g. direct URL paste or pair-success "จัดการ" button from CLI flow).
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.replace('/companies-list');
      }
    },
    async logout() {
      this.loggingOut = true;
      try {
        await firebase.auth().signOut();
        const redirect = encodeURIComponent(this.$route.fullPath);
        this.$router.replace(`/login?redirect=${redirect}`);
      } catch (err) {
        this.error = `ออกจากระบบไม่สำเร็จ: ${err.message}`;
        this.loggingOut = false;
      }
    },
  },
};
</script>

<style scoped>
.daemon-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #eef4fb 0, #f7f9fc 260px, #f7f9fc 100%);
  color: #0f172a;
  font-family: "Noto Sans Thai", "Noto Sans", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.daemon-page :deep(.v-btn:focus-visible),
.daemon-page :deep(.v-input--is-focused .v-input__slot),
.daemon-filter-tab:focus-visible {
  outline: 2px solid #1d4ed8;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .daemon-page *,
  .daemon-page *::before,
  .daemon-page *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

.daemon-shell {
  max-width: 1160px;
  padding: 36px 20px 48px;
}

.daemon-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: start;
  margin-bottom: 20px;
}

.daemon-header__main {
  min-width: 0;
}

.daemon-header__aside {
  display: flex;
  justify-content: flex-end;
}

.daemon-back,
.daemon-refresh,
.daemon-state__action,
.daemon-binding__unbind,
.daemon-confirm__submit,
.daemon-confirm__dismiss {
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.daemon-back {
  margin: 0 0 8px -8px;
}

.daemon-brand {
  display: block;
  height: 32px;
  width: auto;
  margin-bottom: 14px;
  user-select: none;
}

.daemon-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
}

.daemon-kicker__dot {
  width: 3px;
  height: 3px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #94a3b8;
}

.daemon-kicker__step {
  padding: 2px 8px;
  background: #e8f2ff;
  border: 1px solid #cfe1ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  white-space: nowrap;
}

.daemon-header h1 {
  margin: 9px 0 7px;
  color: #0f172a;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.18;
  letter-spacing: 0;
}

.daemon-header p {
  max-width: 690px;
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.65;
}

.daemon-account,
.daemon-stat,
.daemon-panel {
  background: #fff !important;
  border: 1px solid #dbe3ee;
  border-radius: 8px !important;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06) !important;
}

.daemon-account {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 7px 8px 7px 12px;
  font-size: 13px;
}

.daemon-account__dot {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  margin-right: 9px;
  border-radius: 50%;
  background: #16a34a;
}

.daemon-account__email {
  overflow: hidden;
  color: #0f172a;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daemon-stat__label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.daemon-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.daemon-stat {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 14px;
}

.daemon-stat__icon,
.daemon-panel__icon,
.daemon-state__icon,
.daemon-worker__icon,
.daemon-binding__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.daemon-stat__icon {
  width: 42px;
  height: 42px;
}

.daemon-stat__icon--primary,
.daemon-panel__icon {
  background: #e8f2ff;
}

.daemon-stat__icon--cyan {
  background: #e6fffb;
}

.daemon-stat__icon--success {
  background: #dcfce7;
}

.daemon-stat__icon--warning {
  background: #fef3c7;
}

.daemon-stat__value {
  color: #0f172a;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.daemon-panel {
  /* No overflow:hidden — it would clip the sticky toolbar's containing block
     and prevent it from sticking to the viewport on scroll. */
  isolation: isolate;
}

.daemon-panel__header {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
}

.daemon-panel__icon {
  width: 44px;
  height: 44px;
}

.daemon-panel__header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0;
}

.daemon-panel__header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.daemon-panel__header p strong {
  color: #0f172a;
}

.daemon-refresh {
  min-height: 36px;
  padding: 0 14px !important;
  border-radius: 6px !important;
  border: 1px solid #cfe1ff !important;
  background: #fff !important;
  color: #1d4ed8 !important;
  font-weight: 700 !important;
}

.daemon-refresh :deep(.v-btn__content),
.daemon-refresh :deep(.v-icon) {
  color: #1d4ed8 !important;
}

.daemon-refresh:hover {
  background: #eff6ff !important;
  border-color: #1d4ed8 !important;
}

.daemon-toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: grid;
  grid-template-columns: minmax(280px, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 16px 22px;
  background: #fbfdff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 6px 14px -10px rgba(15, 23, 42, 0.18);
}

.daemon-search :deep(.v-input__slot) {
  min-height: 42px !important;
  border-radius: 8px !important;
  background: #fff !important;
}

.daemon-search :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.daemon-filter-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.daemon-filter-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 10px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  color: #475569;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
}

.daemon-filter-tab:hover {
  border-color: #b7c6d9;
  background: #f8fafc;
}

.daemon-filter-tab--active {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}

.daemon-filter-tab strong {
  min-width: 20px;
  padding: 1px 6px;
  background: #f1f5f9;
  border-radius: 999px;
  color: #0f172a;
  text-align: center;
}

.daemon-filter-tab--active strong {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.daemon-inline-code {
  max-width: 100%;
  padding: 1px 6px;
  background: #f1f5f9;
  border-radius: 4px;
  color: #0f172a;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.daemon-panel__body {
  display: grid;
  gap: 18px;
  padding: 22px !important;
}

.daemon-alert {
  margin: 0 !important;
  border-radius: 8px !important;
}

.daemon-alert__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.daemon-skeletons {
  display: grid;
  gap: 14px;
}

.daemon-skeletons__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.daemon-skeleton-worker {
  border: 1px solid #dbe3ee;
  border-left: 4px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
}

.daemon-skeleton-worker__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.daemon-skeleton-worker__title {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.daemon-skeleton-worker__title-text {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.daemon-skeleton-worker__meta {
  display: flex;
  gap: 8px;
}

.daemon-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
}

.daemon-skeleton-row:first-of-type {
  border-top: 0;
}

.daemon-skeleton-row__main {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 1 1 auto;
}

.daemon-skeleton-row__lines {
  display: grid;
  gap: 7px;
  min-width: 0;
  flex: 1 1 auto;
}

.daemon-skeleton {
  background: linear-gradient(
    90deg,
    #eef2f7 0%,
    #f7f9fc 50%,
    #eef2f7 100%
  );
  background-size: 200% 100%;
  border-radius: 6px;
  animation: daemon-shimmer 1.4s ease-in-out infinite;
}

.daemon-skeleton--icon {
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: 8px;
}

.daemon-skeleton--badge {
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border-radius: 8px;
}

.daemon-skeleton--text {
  height: 14px;
  border-radius: 4px;
}

.daemon-skeleton--sm {
  height: 11px;
}

.daemon-skeleton--w-110 { width: 110px; max-width: 100%; }
.daemon-skeleton--w-160 { width: 160px; max-width: 100%; }
.daemon-skeleton--w-180 { width: 180px; max-width: 100%; }
.daemon-skeleton--w-220 { width: 220px; max-width: 100%; }

.daemon-skeleton--pill {
  width: 96px;
  height: 28px;
  border-radius: 999px;
}

.daemon-skeleton--pill-sm {
  width: 64px;
}

.daemon-skeleton--button {
  width: 92px;
  height: 32px;
  flex: 0 0 auto;
}

@keyframes daemon-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .daemon-skeleton {
    animation: none;
    background: #eef2f7;
  }
}

.daemon-state {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 18px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
}

.daemon-state__icon {
  width: 44px;
  height: 44px;
  background: #e2e8f0;
}

.daemon-state h3 {
  margin: 0;
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.4;
}

.daemon-state p {
  margin: 4px 0 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.daemon-state__action {
  margin-top: 10px;
}

.daemon-state__action--primary {
  min-height: 36px;
  padding: 0 16px !important;
  border-radius: 6px !important;
  font-weight: 700 !important;
}

.daemon-workers {
  display: grid;
  gap: 14px;
}

.daemon-worker {
  border: 1px solid #dbe3ee;
  border-left-width: 4px;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.daemon-worker--online {
  border-left-color: #16a34a;
}

.daemon-worker--stale {
  border-left-color: #d97706;
}

.daemon-worker--offline,
.daemon-worker--checking {
  border-left-color: #94a3b8;
}

.daemon-worker__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.daemon-worker__title {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.daemon-worker__title-text {
  min-width: 0;
}

.daemon-worker__icon {
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #dbe3ee;
}

.daemon-worker__name {
  overflow: hidden;
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daemon-worker__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
}

.daemon-worker__dot {
  width: 3px;
  height: 3px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #cbd5e1;
}

.daemon-worker__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.daemon-status-pill,
.daemon-worker__count {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.daemon-status-pill {
  gap: 6px;
}

.daemon-status-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #94a3b8;
}

.daemon-status-pill--online {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.daemon-status-pill--online .daemon-status-pill__dot {
  background: #16a34a;
}

.daemon-status-pill--stale {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.daemon-status-pill--stale .daemon-status-pill__dot {
  background: #d97706;
}

.daemon-status-pill--offline,
.daemon-status-pill--checking {
  background: #f8fafc;
  color: #475569;
}

.daemon-worker__count {
  gap: 4px;
}

.daemon-worker__count strong {
  color: #0f172a;
  font-weight: 800;
}

.daemon-binding-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.daemon-binding {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
}

.daemon-binding:first-child {
  border-top: 0;
}

.daemon-binding:hover {
  background: #fbfdff;
}

.daemon-binding__main {
  min-width: 0;
}

.daemon-binding__title-row {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.daemon-binding__badge {
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  background: #eef6ff;
}

.daemon-binding__name {
  overflow: hidden;
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daemon-binding__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 7px;
  padding-left: 37px;
  color: #64748b;
  font-size: 12px;
}

.daemon-binding__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #64748b;
  font-weight: 700;
}

.daemon-binding__unbind {
  border-radius: 6px !important;
  font-weight: 700 !important;
}

.daemon-confirm {
  border-radius: 12px !important;
  overflow: hidden;
}

.daemon-confirm__title {
  display: flex !important;
  align-items: center;
  gap: 12px;
  padding: 22px 26px 10px !important;
  color: #0f172a;
  font-size: 19px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
  line-height: 1.3 !important;
}

.daemon-confirm__title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  background: #fef2f2;
  border-radius: 10px;
}

.daemon-confirm__body {
  padding: 4px 26px 16px !important;
  color: #334155;
  font-size: 14px;
  line-height: 1.65;
}

.daemon-confirm__lede {
  margin: 0 0 14px !important;
  font-size: 14px;
}

.daemon-confirm__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0 0 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}

.daemon-confirm__hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 6px 0 0 !important;
  color: #64748b;
  font-size: 13px;
  line-height: 1.55;
}

.daemon-confirm__hint .v-icon {
  flex: 0 0 auto;
  margin-top: 2px;
}

.daemon-confirm__actions {
  padding: 10px 20px 18px !important;
  gap: 6px;
  border-top: 1px solid #f1f5f9;
  background: #fbfdff;
}

.daemon-confirm__submit {
  min-height: 38px;
  padding: 0 18px !important;
  border-radius: 6px !important;
  background: #dc2626 !important;
  color: #fff !important;
  font-weight: 700 !important;
  box-shadow: 0 1px 2px rgba(220, 38, 38, 0.25) !important;
}

.daemon-confirm__submit :deep(.v-btn__content),
.daemon-confirm__submit :deep(.v-icon) {
  color: #fff !important;
}

.daemon-confirm__submit:hover {
  background: #b91c1c !important;
}

.daemon-confirm__submit.v-btn--disabled {
  background: #fca5a5 !important;
  color: #fff !important;
}

.daemon-confirm__dismiss {
  min-height: 38px;
  border-radius: 6px !important;
  color: #475569 !important;
  font-weight: 700 !important;
}

.daemon-fade-enter-active,
.daemon-fade-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.daemon-fade-enter,
.daemon-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 960px) {
  .daemon-header {
    grid-template-columns: 1fr;
  }

  .daemon-header__aside {
    justify-content: flex-start;
  }

  .daemon-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .daemon-toolbar {
    grid-template-columns: 1fr;
  }

  .daemon-filter-tabs {
    justify-content: flex-start;
  }
}

@media (max-width: 780px) {
  .daemon-shell {
    padding: 28px 14px 36px;
  }

  .daemon-panel__header {
    grid-template-columns: 44px minmax(0, 1fr);
  }

  .daemon-refresh {
    grid-column: 1 / -1;
    justify-self: end;
  }
}

@media (max-width: 600px) {
  .daemon-stats {
    grid-template-columns: 1fr;
  }

  .daemon-header h1 {
    font-size: 26px;
  }

  .daemon-brand {
    height: 26px;
    margin-bottom: 10px;
  }

  .daemon-account {
    width: 100%;
  }

  .daemon-panel__header,
  .daemon-toolbar,
  .daemon-panel__body {
    padding: 18px !important;
  }

  .daemon-confirm__title,
  .daemon-confirm__body {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .daemon-confirm__actions {
    flex-wrap: wrap;
  }

  .daemon-confirm__submit,
  .daemon-confirm__dismiss {
    flex: 1 1 auto;
  }

  .daemon-worker__header,
  .daemon-binding {
    grid-template-columns: 1fr;
  }

  .daemon-worker__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .daemon-worker__meta {
    justify-content: flex-start;
  }

  .daemon-binding {
    align-items: stretch;
  }

  .daemon-binding__meta {
    padding-left: 0;
  }

  .daemon-binding__unbind {
    justify-self: end;
  }

  .daemon-state {
    grid-template-columns: 1fr;
  }
}
</style>
